/**
 * One number day by day, read at a glance (owner, 2026-10-12: "статистику з графіками по днях… це
 * найважливіша інфа"; then: "палочки-полосочки то добре, але не ясно які там рівні і що вони показують").
 *
 * So every chart says what it shows in words under its title, carries a scale with numbers on the left and
 * dates along the bottom, a dot for every day read, its numbers on the bars, and under it the last day's
 * change and the best day. Pointing at (or tapping) a day adds its date, value and change — an extra: what
 * matters is already written. A day not read yet is a gap, never a zero.
 */

import { useState } from 'react'

import { useT } from '../../i18n'

export interface TrendPoint { at: string; value: number | null }

const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
const short = (v: number) => (Math.abs(v) >= 10000
  ? new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(v)
  : v.toLocaleString())
const signed = (v: number) => `${v > 0 ? '+' : ''}${v.toLocaleString()}`

/** A scale a person reads without thinking: three round levels covering lo..hi. */
function scale(lo: number, hi: number): [number, number, number] {
  const counts = lo >= 0
  if (hi === lo) {
    const pad = Math.max(1, Math.abs(hi) * 0.1)
    lo -= pad
    hi += pad
  }
  const raw = (hi - lo) / 2
  const mag = 10 ** Math.floor(Math.log10(raw))
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw
  // counts never go below zero: a scale reading "−1 follower" says nothing true
  const bottom = Math.max(counts ? 0 : -Infinity, Math.floor(lo / step) * step)
  return [bottom, bottom + step, bottom + 2 * step]
}

export function TrendChart({ title, hint, points, kind = 'line' }: {
  title: string
  /** what the number is, in words ("followers at the end of each day") */
  hint?: string
  points: TrendPoint[]
  /** `bars` — a count per day (posts), not a level */
  kind?: 'line' | 'bars'
}) {
  const t = useT().trend
  const [hover, setHover] = useState<number | null>(null)
  const known = points.map((p, i) => ({ ...p, i })).filter((p): p is { at: string; value: number; i: number } => p.value != null)
  const last = known[known.length - 1]
  const first = known[0]
  const W = 300
  const H = 120
  const n = points.length
  const values = known.map((p) => p.value)
  const lo = kind === 'bars' || values.length === 0 ? 0 : Math.min(...values)
  const hi = values.length === 0 ? 1 : Math.max(...values, kind === 'bars' ? 1 : -Infinity)
  const [l0, l1, l2] = scale(lo, hi)
  const step = (W - 16) / Math.max(n - 1, 1)
  const x = (i: number) => (n === 1 ? W / 2 : 8 + i * step)
  const y = (v: number) => H - 4 - ((v - l0) / (l2 - l0 || 1)) * (H - 12)
  const total = kind === 'bars' ? values.reduce((s, v) => s + v, 0) : null
  const gain = kind === 'line' && last && first && known.length > 1 ? last.value - first.value : null

  // changes from one read day to the next
  const changes = known.slice(1).map((p, k) => ({ at: p.at, i: p.i, d: p.value - known[k]!.value }))
  const lastChange = changes[changes.length - 1]
  const best = kind === 'line'
    ? changes.reduce<typeof changes[number] | null>((b, c) => (c.d > 0 && (!b || c.d > b.d) ? c : b), null)
    : known.reduce<typeof known[number] | null>((b, p) => (p.value > 0 && (!b || p.value > b.value) ? p : b), null)

  let d = ''
  let open = false
  points.forEach((p, i) => {
    if (p.value == null) { open = false; return }
    d += `${open ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.value).toFixed(1)} `
    open = true
  })

  // dates along the bottom: four at most (they must not run into each other on a narrow card), always
  // the first and the last
  const every = n <= 4 ? 1 : Math.ceil((n - 1) / 3)
  const ticks = points.map((p, i) => ({ p, i })).filter(({ i }) => i % every === 0 || i === n - 1)
    .filter(({ i }, k, all) => i === n - 1 || all[k + 1] === undefined || all[k + 1]!.i - i >= every * 0.6)
  const barW = Math.max(2, Math.min(step * 0.7, 22))
  const showBarNumbers = kind === 'bars' && n <= 14
  const showDots = kind === 'line' && n <= 45

  const h = hover != null ? points[hover] : null
  const hPrev = hover != null ? [...known].reverse().find((p) => p.i < hover) : undefined
  const pick = (clientX: number, box: DOMRect) => {
    const i = Math.round((((clientX - box.left) / box.width) * W - 8) / step)
    setHover(Math.max(0, Math.min(n - 1, n === 1 ? 0 : i)))
  }

  return (
    <div className="trend">
      <div className="trend-head">
        <span className="trend-title">{title}</span>
        {known.length === 0
          ? <span className="tiny dim">{t.notRead}</span>
          : <strong className="trend-value">{(total ?? last!.value).toLocaleString()}</strong>}
        {gain !== null && (
          <span className={`tiny ${gain > 0 ? 'ok-text' : gain < 0 ? 'bad-text' : 'dim'}`}>
            {signed(gain)} {t.since(day(first!.at))}
          </span>
        )}
        {total !== null && known.length > 0 && <span className="tiny dim">{t.overDays(n)}</span>}
      </div>
      {hint && <div className="trend-hint tiny dim">{hint}</div>}
      {known.length > 0 && (
        <div className="trend-plot">
          <div className="trend-y tiny" aria-hidden>
            <span>{short(l2)}</span><span>{short(l1)}</span><span>{short(l0)}</span>
          </div>
          <div className="trend-area">
            <svg className="trend-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img"
                 aria-label={`${title}: ${points.map((p) => `${day(p.at)} ${p.value ?? '—'}`).join(', ')}`}
                 onPointerMove={(e) => pick(e.clientX, e.currentTarget.getBoundingClientRect())}
                 onPointerDown={(e) => pick(e.clientX, e.currentTarget.getBoundingClientRect())}
                 onPointerLeave={() => setHover(null)}>
              {[l0, l1, l2].map((v) => <line key={v} x1="0" x2={W} y1={y(v)} y2={y(v)} className={v === l0 ? 'trend-base' : 'trend-grid-line'} />)}
              {kind === 'bars' && points.map((p, i) => p.value != null && p.value > 0 && (
                <rect key={i} x={x(i) - barW / 2} width={barW} y={y(p.value)} height={y(l0) - y(p.value)}
                      className={`trend-bar${hover === i ? ' on' : ''}`} rx="2" />
              ))}
              {kind === 'line' && known.length > 1 && (
                <path d={`${d} L${x(last!.i).toFixed(1)},${y(l0)} L${x(first!.i).toFixed(1)},${y(l0)} Z`} className="trend-fill" />
              )}
              {kind === 'line' && <path d={d} className="trend-line" />}
              {hover != null && <line x1={x(hover)} x2={x(hover)} y1="0" y2={H} className="trend-cursor" />}
            </svg>
            {/* dots and numbers as HTML over the picture, so they stay round and readable at any width */}
            {kind === 'line' && !showDots && last && (
              <span className="trend-pt last" style={{ left: `${(x(last.i) / W) * 100}%`, top: `${(y(last.value) / H) * 100}%` }} />
            )}
            {showDots && known.map((p) => (
              <span key={p.i} className={`trend-pt${p.i === last!.i ? ' last' : ''}${hover === p.i ? ' on' : ''}`}
                    style={{ left: `${(x(p.i) / W) * 100}%`, top: `${(y(p.value) / H) * 100}%` }} />
            ))}
            {showBarNumbers && known.filter((p) => p.value > 0).map((p) => (
              <span key={p.i} className="trend-num tiny" style={{ left: `${(x(p.i) / W) * 100}%`, top: `${(y(p.value) / H) * 100}%` }}>
                {p.value}
              </span>
            ))}
            {h && (
              <div className={`trend-tip tiny${hover! > n / 2 ? ' left' : ''}`} style={{ left: `${(x(hover!) / W) * 100}%` }}>
                <b>{day(h.at)}</b>
                <span>{h.value == null ? t.notRead : h.value.toLocaleString()}</span>
                {kind === 'line' && h.value != null && hPrev && (
                  <span className={h.value - hPrev.value > 0 ? 'ok-text' : h.value - hPrev.value < 0 ? 'bad-text' : 'dim'}>
                    {t.dayChange(signed(h.value - hPrev.value))}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="trend-x tiny dim" aria-hidden>
            {ticks.map(({ p, i }) => (
              <span key={i} style={{ left: `${(x(i) / W) * 100}%` }} className={i === 0 ? 'first' : i === n - 1 ? 'last' : ''}>{day(p.at)}</span>
            ))}
          </div>
        </div>
      )}
      {kind === 'line' && known.length === 1 && (
        <div className="trend-foot tiny dim">{t.firstReading(day(first!.at))}</div>
      )}
      {known.length > 0 && (
        <div className="trend-foot tiny">
          {kind === 'line' && lastChange && (
            <span>{t.lastDay(day(lastChange.at))} <b className={lastChange.d > 0 ? 'ok-text' : lastChange.d < 0 ? 'bad-text' : ''}>{signed(lastChange.d)}</b></span>
          )}
          {best && (
            <span className="dim">{t.bestDay(day(best.at), kind === 'line' ? signed((best as { d: number }).d) : String((best as { value: number }).value))}</span>
          )}
        </div>
      )}
    </div>
  )
}
